// =============================================================
// SEOIntelligencePage  Phase 5B
// Local SEO Opportunity & AI Analyst Dashboard
// View deterministic SEO opportunities and generate verified AI insights.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  intelligenceApi,
  aiApi,
  Business,
  SEOOpportunityDto,
  OpportunityEngineSummaryDto,
  OpportunityCategory,
  OpportunityPriority,
  OpportunityStatus,
  AIOpportunityAnalysisResult,
  AITitleSuggestionsResult,
  AIMetaSuggestionsResult,
  AIContentBriefResult,
  AIWeeklySummaryResult,
} from '../lib/api';

export default function SEOIntelligencePage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const parsedBizId = businessId ? parseInt(businessId, 10) : undefined;

  const [business, setBusiness] = useState<Business | null>(null);
  const [summary, setSummary] = useState<OpportunityEngineSummaryDto | null>(null);
  const [opportunities, setOpportunities] = useState<SEOOpportunityDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('OPEN');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  // Selected opportunity for Evidence Modal
  const [selectedOpportunity, setSelectedOpportunity] = useState<SEOOpportunityDto | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // Phase 5B AI State
  const [aiAnalyses, setAiAnalyses] = useState<Record<number, AIOpportunityAnalysisResult>>({});
  const [titleSuggestions, setTitleSuggestions] = useState<Record<number, AITitleSuggestionsResult>>({});
  const [metaSuggestions, setMetaSuggestions] = useState<Record<number, AIMetaSuggestionsResult>>({});
  const [contentBriefs, setContentBriefs] = useState<Record<number, AIContentBriefResult>>({});

  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const [generatingTitleId, setGeneratingTitleId] = useState<number | null>(null);
  const [generatingMetaId, setGeneratingMetaId] = useState<number | null>(null);
  const [generatingBriefId, setGeneratingBriefId] = useState<number | null>(null);

  // Weekly Summary Modal
  const [weeklySummary, setWeeklySummary] = useState<AIWeeklySummaryResult | null>(null);
  const [loadingWeeklySummary, setLoadingWeeklySummary] = useState(false);
  const [showWeeklySummaryModal, setShowWeeklySummaryModal] = useState(false);

  // Copied text notification
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!parsedBizId) return;
    setLoading(true);
    setError(null);
    setSummary(null);
    setOpportunities([]);

    try {
      // 1. Fetch Business
      const biz = await businessApi.get(parsedBizId);
      setBusiness(biz);

      // 2. Fetch Summary
      try {
        const sum = await intelligenceApi.getSummary(parsedBizId);
        setSummary(sum);
      } catch {
        // May have no opportunities generated yet
      }

      // 3. Fetch Opportunities
      const filters: any = {};
      if (statusFilter && statusFilter !== 'ALL') filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (priorityFilter) filters.priority = priorityFilter;

      const opps = await intelligenceApi.getOpportunities(parsedBizId, filters);
      setOpportunities(opps);
    } catch (err: any) {
      console.error('Failed to load SEO Intelligence data:', err);
      setError(err.message || 'Failed to load SEO Intelligence data.');
    } finally {
      setLoading(false);
    }
  }, [parsedBizId, statusFilter, categoryFilter, priorityFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Synchronize immediately if business is changed via TopBar dropdown
  useEffect(() => {
    function handleBizChange(e: Event) {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail && customEvent.detail !== parsedBizId) {
        setOpportunities([]);
        setSummary(null);
        navigate(`/businesses/${customEvent.detail}/intelligence`);
      }
    }
    window.addEventListener('active-business-change', handleBizChange);
    return () => window.removeEventListener('active-business-change', handleBizChange);
  }, [parsedBizId, navigate]);

  // Generate / Refresh Engine
  const handleGenerate = async () => {
    if (!parsedBizId) return;
    setGenerating(true);
    setError(null);
    setActionSuccess(null);

    try {
      const res = await intelligenceApi.generate(parsedBizId);
      setActionSuccess(`Successfully analyzed stored data and refreshed ${res.generatedCount} SEO opportunities.`);
      await loadData();
    } catch (err: any) {
      console.error('Error generating opportunities:', err);
      setError(err.message || 'Failed to generate opportunities.');
    } finally {
      setGenerating(false);
    }
  };

  // Status transitions
  const handleUpdateStatus = async (id: number, newStatus: OpportunityStatus) => {
    if (!parsedBizId) return;
    setUpdatingId(id);
    try {
      await intelligenceApi.updateStatus(parsedBizId, id, newStatus);
      await loadData();
      if (selectedOpportunity && selectedOpportunity.id === id) {
        setSelectedOpportunity((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(err.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  // AI Analysis
  const handleAnalyzeWithAI = async (oppId: number, force = false) => {
    if (!parsedBizId) return;
    setAnalyzingId(oppId);
    setError(null);

    try {
      const res = await aiApi.analyzeOpportunity(parsedBizId, oppId, force);
      setAiAnalyses((prev) => ({ ...prev, [oppId]: res.analysis }));
      setActionSuccess(res.cached ? 'Loaded cached AI analysis.' : 'Generated fresh AI analysis.');
    } catch (err: any) {
      console.error('AI Analysis failed:', err);
      if (err.status === 503 || err.code === 'AI_NOT_CONFIGURED') {
        setError('AI analysis is not configured yet.');
      } else {
        setError(err.message || 'AI analysis failed.');
      }
    } finally {
      setAnalyzingId(null);
    }
  };

  // Title Suggestions
  const handleGenerateTitleSuggestions = async (oppId: number, force = false) => {
    if (!parsedBizId) return;
    setGeneratingTitleId(oppId);
    setError(null);

    try {
      const res = await aiApi.generateTitleSuggestions(parsedBizId, oppId, force);
      setTitleSuggestions((prev) => ({ ...prev, [oppId]: res.suggestions }));
      setActionSuccess(res.cached ? 'Loaded cached title suggestions.' : 'Generated 3 title suggestions.');
    } catch (err: any) {
      console.error('Title generation failed:', err);
      if (err.status === 503 || err.code === 'AI_NOT_CONFIGURED') {
        setError('AI analysis is not configured yet.');
      } else {
        setError(err.message || 'Failed to generate title suggestions.');
      }
    } finally {
      setGeneratingTitleId(null);
    }
  };

  // Meta Suggestions
  const handleGenerateMetaSuggestions = async (oppId: number, force = false) => {
    if (!parsedBizId) return;
    setGeneratingMetaId(oppId);
    setError(null);

    try {
      const res = await aiApi.generateMetaSuggestions(parsedBizId, oppId, force);
      setMetaSuggestions((prev) => ({ ...prev, [oppId]: res.suggestions }));
      setActionSuccess(res.cached ? 'Loaded cached meta descriptions.' : 'Generated 3 meta description options.');
    } catch (err: any) {
      console.error('Meta generation failed:', err);
      if (err.status === 503 || err.code === 'AI_NOT_CONFIGURED') {
        setError('AI analysis is not configured yet.');
      } else {
        setError(err.message || 'Failed to generate meta suggestions.');
      }
    } finally {
      setGeneratingMetaId(null);
    }
  };

  // Content Brief
  const handleGenerateContentBrief = async (oppId: number, force = false) => {
    if (!parsedBizId) return;
    setGeneratingBriefId(oppId);
    setError(null);

    try {
      const res = await aiApi.generateContentBrief(parsedBizId, oppId, force);
      setContentBriefs((prev) => ({ ...prev, [oppId]: res.brief }));
      setActionSuccess(res.cached ? 'Loaded cached content brief.' : 'Generated Local SEO Content Brief.');
    } catch (err: any) {
      console.error('Content brief generation failed:', err);
      if (err.status === 503 || err.code === 'AI_NOT_CONFIGURED') {
        setError('AI analysis is not configured yet.');
      } else {
        setError(err.message || 'Failed to generate content brief.');
      }
    } finally {
      setGeneratingBriefId(null);
    }
  };

  // Weekly Summary
  const handleWeeklySummary = async (force = false) => {
    if (!parsedBizId) return;
    setLoadingWeeklySummary(true);
    setError(null);

    try {
      const res = await aiApi.generateWeeklySummary(parsedBizId, force);
      setWeeklySummary(res.summary);
      setShowWeeklySummaryModal(true);
    } catch (err: any) {
      console.error('Weekly summary generation failed:', err);
      if (err.status === 503 || err.code === 'AI_NOT_CONFIGURED') {
        setError('AI analysis is not configured yet.');
      } else {
        setError(err.message || 'Failed to generate weekly summary.');
      }
    } finally {
      setLoadingWeeklySummary(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2500);
  };

  const getPriorityBadgeStyle = (priority: OpportunityPriority) => {
    switch (priority) {
      case 'CRITICAL':
        return { background: '#fee2e2', color: '#991b1b', border: '1px solid #f87171' };
      case 'HIGH':
        return { background: '#ffedd5', color: '#9a3412', border: '1px solid #fb923c' };
      case 'MEDIUM':
        return { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
      case 'LOW':
        return { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' };
    }
  };

  const getCategoryLabel = (category: OpportunityCategory) => {
    switch (category) {
      case 'ON_PAGE':
        return 'On-Page SEO';
      case 'LOCAL_SEO':
        return 'Local SEO';
      case 'SEARCH_CONSOLE':
        return 'Search Console';
      case 'GOOGLE_BUSINESS_PROFILE':
        return 'Google Business Profile';
      case 'TECHNICAL':
        return 'Technical SEO';
      case 'CONTENT':
        return 'Content Strategy';
      default:
        return category;
    }
  };

  return (
    <DashboardLayout
      title={business ? `${business.name}  SEO Intelligence & AI Analyst` : 'SEO Intelligence'}
      subtitle="Deterministic, explainable Local SEO opportunities with structured AI analysis backed by verified database signals."
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        {
          label: business?.name || 'Business',
          to: business ? `/businesses/${business.id}/dashboard` : '/businesses',
        },
        { label: 'SEO Intelligence' },
      ]}
    >
      <div className="dashboard-container" style={{ maxWidth: '1120px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Navigation Breadcrumb */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#64748b' }}>
          <Link to="/dashboard" style={{ color: '#64748b', textDecoration: 'none' }}>
            Dashboard
          </Link>
          <span>/</span>
          {business ? (
            <Link to={`/businesses/${business.id}/dashboard`} style={{ color: '#64748b', textDecoration: 'none' }}>
              {business.name}
            </Link>
          ) : (
            <span>Business</span>
          )}
          <span>/</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>SEO Intelligence</span>
        </div>

        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              SEO Intelligence & AI Analyst
            </h1>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.95rem' }}>
              Verified signals converted into prioritized SEO opportunities with structured AI recommendations. No hallucinations.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {business && (
              <Link
                to={`/businesses/${business.id}/dashboard`}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', padding: '8px 14px' }}
              >
                <span></span>
                <span>Dashboard</span>
              </Link>
            )}

            <button
              onClick={() => handleWeeklySummary(false)}
              disabled={loadingWeeklySummary}
              className="btn btn-secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 16px',
                fontWeight: 600,
                fontSize: '0.92rem',
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                cursor: loadingWeeklySummary ? 'not-allowed' : 'pointer',
              }}
            >
              {loadingWeeklySummary ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Generating Summary...</span>
                </>
              ) : (
                <>
                  <span></span>
                  <span>Weekly Executive AI Summary</span>
                </>
              )}
            </button>

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                fontWeight: 600,
                fontSize: '0.92rem',
                backgroundColor: '#2563eb',
                color: '#fff',
                borderRadius: '8px',
                border: 'none',
                cursor: generating ? 'not-allowed' : 'pointer',
              }}
            >
              {generating ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Analyzing Data...</span>
                </>
              ) : (
                <>
                  <span></span>
                  <span>Refresh Opportunities</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Notifications */}
        {actionSuccess && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              color: '#166534',
              fontSize: '0.92rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span></span>
              <span>{actionSuccess}</span>
            </div>
            <button
              onClick={() => setActionSuccess(null)}
              style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: '1.1rem' }}
            >
              
            </button>
          </div>
        )}

        {copiedText && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 99999,
              backgroundColor: '#0f172a',
              color: '#38bdf8',
              padding: '10px 18px',
              borderRadius: '8px',
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3)',
              fontSize: '0.88rem',
              fontWeight: 600,
            }}
          >
             Copied to clipboard!
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '0.92rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span></span>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '1.1rem' }}
            >
              
            </button>
          </div>
        )}

        {/* Score & Summary KPI Card */}
        {summary && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '28px',
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background:
                    summary.opportunityScore >= 80
                      ? '#dcfce7'
                      : summary.opportunityScore >= 50
                      ? '#fef3c7'
                      : '#fee2e2',
                  color:
                    summary.opportunityScore >= 80
                      ? '#166534'
                      : summary.opportunityScore >= 50
                      ? '#92400e'
                      : '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {summary.opportunityScore}
              </div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                  Opportunity Score
                </div>
                <div style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: 500, marginTop: '2px' }}>
                  {summary.opportunityScore >= 80 ? 'Optimal' : summary.opportunityScore >= 50 ? 'Moderate Issues' : 'Needs Attention'}
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fee2e2', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#991b1b', textTransform: 'uppercase' }}>Critical</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#991b1b', marginTop: '4px' }}>{summary.criticalCount}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Immediate action needed</div>
            </div>

            <div style={{ backgroundColor: '#ffffff', border: '1px solid #ffedd5', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#9a3412', textTransform: 'uppercase' }}>High Priority</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#ea580c', marginTop: '4px' }}>{summary.highCount}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>High business value</div>
            </div>

            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fef3c7', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#92400e', textTransform: 'uppercase' }}>Medium Priority</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#d97706', marginTop: '4px' }}>{summary.mediumCount}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Best practice fixes</div>
            </div>

            <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>Low Priority</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#475569', marginTop: '4px' }}>{summary.lowCount}</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Minor enhancements</div>
            </div>
          </div>
        )}

        {/* Filters and Search Bar */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Status Tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { key: 'OPEN', label: 'Open', count: summary?.openCount ?? 0 },
              { key: 'IN_PROGRESS', label: 'In Progress', count: summary?.inProgressCount ?? 0 },
              { key: 'COMPLETED', label: 'Completed', count: summary?.completedCount ?? 0 },
              { key: 'DISMISSED', label: 'Dismissed', count: summary?.dismissedCount ?? 0 },
              { key: 'ALL', label: 'All', count: summary?.totalOpportunities ?? 0 },
            ].map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    border: isActive ? '1px solid #2563eb' : '1px solid #e2e8f0',
                    backgroundColor: isActive ? '#eff6ff' : '#f8fafc',
                    color: isActive ? '#1d4ed8' : '#64748b',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      padding: '0 6px',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      backgroundColor: isActive ? '#2563eb' : '#e2e8f0',
                      color: isActive ? '#ffffff' : '#64748b',
                      lineHeight: 1,
                    }}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Dropdown Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.88rem',
                color: '#334155',
                backgroundColor: '#ffffff',
                outline: 'none',
              }}
            >
              <option value="">All Categories</option>
              <option value="ON_PAGE">On-Page SEO</option>
              <option value="LOCAL_SEO">Local SEO</option>
              <option value="SEARCH_CONSOLE">Search Console</option>
              <option value="GOOGLE_BUSINESS_PROFILE">Google Business Profile</option>
              <option value="TECHNICAL">Technical SEO</option>
              <option value="CONTENT">Content Strategy</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              style={{
                padding: '7px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.88rem',
                color: '#334155',
                backgroundColor: '#ffffff',
                outline: 'none',
              }}
            >
              <option value="">All Priorities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
        </div>

        {/* Opportunities List */}
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <div className="spinner-border text-primary" role="status" style={{ marginBottom: '12px' }}></div>
            <div>Loading SEO opportunities...</div>
          </div>
        ) : opportunities.length === 0 ? (
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px dashed #cbd5e1',
              borderRadius: '12px',
              padding: '48px 24px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}></div>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', color: '#0f172a' }}>
              No {statusFilter !== 'ALL' ? statusFilter.toLowerCase() : ''} opportunities found
            </h3>
            <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.92rem', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
              {summary && summary.totalOpportunities > 0
                ? 'Try adjusting your filters above or generate a fresh batch using current website and Google metrics.'
                : 'Click the button below to analyze website audits, Search Console queries, and Google Business Profile data.'}
            </p>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn btn-primary"
              style={{
                padding: '9px 18px',
                fontWeight: 600,
                backgroundColor: '#2563eb',
                color: '#fff',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {generating ? 'Analyzing Stored Data...' : ' Generate SEO Opportunities'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {opportunities.map((opp) => {
              const priorityStyle = getPriorityBadgeStyle(opp.priority);
              const isUpdating = updatingId === opp.id;
              const isAnalyzing = analyzingId === opp.id;
              const isGeneratingTitle = generatingTitleId === opp.id;
              const isGeneratingMeta = generatingMetaId === opp.id;
              const isGeneratingBrief = generatingBriefId === opp.id;

              const aiAnalysis = aiAnalyses[opp.id];
              const titles = titleSuggestions[opp.id];
              const metas = metaSuggestions[opp.id];
              const brief = contentBriefs[opp.id];

              const isTitleOpp =
                opp.type === 'MISSING_TITLE' ||
                opp.title.toLowerCase().includes('title') ||
                opp.category === 'ON_PAGE';
              const isMetaOpp =
                opp.type === 'MISSING_META_DESCRIPTION' ||
                opp.title.toLowerCase().includes('meta');
              const isContentOpp =
                opp.type === 'LOCAL_SEARCH_OPPORTUNITY' ||
                opp.category === 'LOCAL_SEO' ||
                opp.category === 'CONTENT';

              return (
                <div
                  key={opp.id}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}
                >
                  {/* Top Bar: Badges and Status */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          ...priorityStyle,
                          padding: '3px 10px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          letterSpacing: '0.4px',
                        }}
                      >
                        {opp.priority}
                      </span>

                      <span
                        style={{
                          backgroundColor: '#f1f5f9',
                          color: '#334155',
                          padding: '3px 10px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                        }}
                      >
                        {getCategoryLabel(opp.category)}
                      </span>

                      <span
                        style={{
                          backgroundColor:
                            opp.status === 'OPEN'
                              ? '#dbeafe'
                              : opp.status === 'IN_PROGRESS'
                              ? '#fef3c7'
                              : opp.status === 'COMPLETED'
                              ? '#dcfce7'
                              : '#f1f5f9',
                          color:
                            opp.status === 'OPEN'
                              ? '#1e40af'
                              : opp.status === 'IN_PROGRESS'
                              ? '#92400e'
                              : opp.status === 'COMPLETED'
                              ? '#166534'
                              : '#64748b',
                          padding: '3px 10px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                        }}
                      >
                        {opp.status.replace('_', ' ')}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.82rem', color: '#64748b' }}>
                      <div>
                        Impact: <strong style={{ color: '#0f172a' }}>{opp.impactScore}</strong>/100
                      </div>
                      <div></div>
                      <div>
                        Confidence: <strong style={{ color: '#0f172a' }}>{opp.confidenceScore}</strong>/100
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 style={{ margin: '0 0 8px', fontSize: '1.22rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                    {opp.title}
                  </h3>

                  {/* Target Keyword or URL */}
                  {(opp.targetKeyword || opp.targetUrl) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px', fontSize: '0.85rem' }}>
                      {opp.targetKeyword && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#475569' }}>
                          <span style={{ color: '#64748b' }}>Query:</span>
                          <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', color: '#0f172a' }}>
                            "{opp.targetKeyword}"
                          </code>
                        </div>
                      )}
                      {opp.targetUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#475569' }}>
                          <span style={{ color: '#64748b' }}>Page:</span>
                          <span style={{ background: '#f8fafc', padding: '2px 6px', borderRadius: '4px', color: '#2563eb', textDecoration: 'underline' }}>
                            {opp.targetUrl}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Evidence Snapshot */}
                  <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                      Evidence & Stored Signals
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '0.88rem', color: '#334155' }}>
                      {opp.evidenceJson.impressions !== undefined && (
                        <div>Impressions: <strong>{Number(opp.evidenceJson.impressions).toLocaleString()}</strong></div>
                      )}
                      {opp.evidenceJson.clicks !== undefined && (
                        <div>Clicks: <strong>{Number(opp.evidenceJson.clicks).toLocaleString()}</strong></div>
                      )}
                      {opp.evidenceJson.ctr !== undefined && (
                        <div>CTR: <strong>{(Number(opp.evidenceJson.ctr) * 100).toFixed(2)}%</strong></div>
                      )}
                      {opp.evidenceJson.position !== undefined && (
                        <div>Position: <strong>{Number(opp.evidenceJson.position).toFixed(1)}</strong></div>
                      )}
                      {opp.evidenceJson.findingCode && (
                        <div>Finding: <code>{String(opp.evidenceJson.findingCode)}</code></div>
                      )}
                      {opp.evidenceJson.websiteClicks !== undefined && (
                        <div>GBP Website Clicks: <strong>{String(opp.evidenceJson.websiteClicks)}</strong></div>
                      )}
                      {opp.evidenceJson.searchImpressions !== undefined && (
                        <div>GBP Search Impressions: <strong>{String(opp.evidenceJson.searchImpressions)}</strong></div>
                      )}
                    </div>
                  </div>

                  {/* Why this matters */}
                  <div style={{ marginBottom: '12px', fontSize: '0.92rem', color: '#334155', lineHeight: 1.5 }}>
                    <strong style={{ color: '#0f172a' }}>Why this matters: </strong>
                    {opp.description}
                  </div>

                  {/* Recommended Action */}
                  <div style={{ marginBottom: '16px', fontSize: '0.92rem', color: '#166534', backgroundColor: '#f0fdf4', padding: '10px 14px', borderRadius: '8px', border: '1px solid #bbf7d0', lineHeight: 1.5 }}>
                    <strong>Recommended action: </strong>
                    {opp.recommendation}
                  </div>

                  {/* AI Analysis Result Display */}
                  {aiAnalysis && (
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, color: '#1e293b' }}>
                          <span></span>
                          <span>AI Analyst Breakdown (Verified Signals)</span>
                        </div>
                        <button
                          onClick={() => handleAnalyzeWithAI(opp.id, true)}
                          disabled={isAnalyzing}
                          style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Refresh AI Analysis
                        </button>
                      </div>

                      <div style={{ fontSize: '0.9rem', color: '#334155', marginBottom: '8px' }}>
                        <strong>Summary: </strong>{aiAnalysis.summary}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#334155', marginBottom: '10px' }}>
                        <strong>Why It Matters: </strong>{aiAnalysis.whyItMatters}
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ fontSize: '0.85rem', color: '#0f172a' }}>Recommended Step-by-Step Actions:</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: '20px', fontSize: '0.88rem', color: '#334155' }}>
                          {aiAnalysis.recommendedActions.map((act, i) => (
                            <li key={i} style={{ marginBottom: '4px' }}>{act}</li>
                          ))}
                        </ul>
                      </div>

                      {aiAnalysis.risks && aiAnalysis.risks.length > 0 && (
                        <div style={{ marginBottom: '10px', background: '#fffbeb', border: '1px solid #fef3c7', padding: '8px 12px', borderRadius: '6px' }}>
                          <strong style={{ fontSize: '0.82rem', color: '#92400e' }}> Risks & Considerations:</strong>
                          <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '0.85rem', color: '#78350f' }}>
                            {aiAnalysis.risks.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ fontSize: '0.85rem', color: '#1d4ed8', background: '#eff6ff', padding: '8px 12px', borderRadius: '6px' }}>
                        <strong>Suggested Immediate Next Step: </strong>{aiAnalysis.suggestedNextStep}
                      </div>
                    </div>
                  )}

                  {/* Title Suggestions Display */}
                  {titles && (
                    <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
                      <div style={{ fontWeight: 700, color: '#166534', marginBottom: '10px' }}>
                         3 Title Suggestions (Length & Intent Optimized)
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {titles.suggestions.map((s, idx) => (
                          <div key={idx} style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{s.title}</strong>
                              <button
                                onClick={() => copyToClipboard(s.title)}
                                style={{ padding: '3px 8px', fontSize: '0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer' }}
                              >
                                 Copy
                              </button>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569' }}>{s.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Meta Suggestions Display */}
                  {metas && (
                    <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
                      <div style={{ fontWeight: 700, color: '#166534', marginBottom: '10px' }}>
                         3 Meta Description Alternatives (140-160 chars)
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {metas.suggestions.map((s, idx) => (
                          <div key={idx} style={{ background: '#ffffff', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '0.92rem', color: '#0f172a' }}>"{s.metaDescription}"</span>
                              <button
                                onClick={() => copyToClipboard(s.metaDescription)}
                                style={{ padding: '3px 8px', fontSize: '0.75rem', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', flexShrink: 0 }}
                              >
                                 Copy
                              </button>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569' }}>{s.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content Brief Display */}
                  {brief && (
                    <div style={{ backgroundColor: '#fdf4ff', border: '1px solid #f0abfc', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
                      <div style={{ fontWeight: 700, color: '#86198f', marginBottom: '10px' }}>
                         Local SEO Content Brief: {brief.suggestedPageTopic}
                      </div>
                      <div style={{ fontSize: '0.88rem', color: '#4a044e', marginBottom: '8px' }}>
                        <strong>Intent: </strong>{brief.searchIntent}  <strong>H1 Headline: </strong><code>{brief.suggestedH1}</code>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <strong style={{ fontSize: '0.85rem', color: '#701a75' }}>Section Outline:</strong>
                        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {brief.sectionOutline.map((sec, i) => (
                            <div key={i} style={{ background: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #f5d0fe' }}>
                              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#701a75' }}>{sec.heading}</div>
                              <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: '#475569' }}>
                                {sec.keyPoints.map((kp, j) => <li key={j}>{kp}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      {brief.localInformationToInclude.length > 0 && (
                        <div style={{ fontSize: '0.85rem', marginBottom: '8px' }}>
                          <strong style={{ color: '#701a75' }}>Local Details to Include: </strong>
                          <span>{brief.localInformationToInclude.join('  ')}</span>
                        </div>
                      )}

                      {brief.faqIdeas.length > 0 && (
                        <div>
                          <strong style={{ fontSize: '0.85rem', color: '#701a75' }}>FAQ Ideas:</strong>
                          <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: '#475569' }}>
                            {brief.faqIdeas.map((f, i) => (
                              <li key={i}><strong>{f.question}</strong>  {f.answerGuidance}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI & Standard Action Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' }}>
                    {/* AI Buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleAnalyzeWithAI(opp.id, false)}
                        disabled={isAnalyzing}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #818cf8',
                          backgroundColor: '#eef2ff',
                          color: '#4338ca',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isAnalyzing ? 'Analyzing with AI...' : ' Analyze with AI'}
                      </button>

                      {isTitleOpp && (
                        <button
                          onClick={() => handleGenerateTitleSuggestions(opp.id, false)}
                          disabled={isGeneratingTitle}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #86efac',
                            backgroundColor: '#f0fdf4',
                            color: '#166534',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: isGeneratingTitle ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isGeneratingTitle ? 'Generating Titles...' : ' Generate Titles'}
                        </button>
                      )}

                      {isMetaOpp && (
                        <button
                          onClick={() => handleGenerateMetaSuggestions(opp.id, false)}
                          disabled={isGeneratingMeta}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #86efac',
                            backgroundColor: '#f0fdf4',
                            color: '#166534',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: isGeneratingMeta ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isGeneratingMeta ? 'Generating Metas...' : ' Generate Meta'}
                        </button>
                      )}

                      {isContentOpp && (
                        <button
                          onClick={() => handleGenerateContentBrief(opp.id, false)}
                          disabled={isGeneratingBrief}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #f0abfc',
                            backgroundColor: '#fdf4ff',
                            color: '#86198f',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: isGeneratingBrief ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isGeneratingBrief ? 'Building Brief...' : ' Content Brief'}
                        </button>
                      )}
                    </div>

                    {/* Standard Opportunity Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setSelectedOpportunity(opp)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: '#ffffff',
                          color: '#334155',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                         Evidence
                      </button>

                      {opp.status === 'OPEN' && (
                        <button
                          onClick={() => handleUpdateStatus(opp.id, 'IN_PROGRESS')}
                          disabled={isUpdating}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #2563eb',
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Mark In Progress
                        </button>
                      )}

                      {opp.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => handleUpdateStatus(opp.id, 'COMPLETED')}
                          disabled={isUpdating}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #16a34a',
                            backgroundColor: '#16a34a',
                            color: '#ffffff',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Mark Completed
                        </button>
                      )}

                      {(opp.status === 'OPEN' || opp.status === 'IN_PROGRESS') && (
                        <button
                          onClick={() => handleUpdateStatus(opp.id, 'DISMISSED')}
                          disabled={isUpdating}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#64748b',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                          }}
                        >
                          Dismiss
                        </button>
                      )}

                      {(opp.status === 'COMPLETED' || opp.status === 'DISMISSED') && (
                        <button
                          onClick={() => handleUpdateStatus(opp.id, 'OPEN')}
                          disabled={isUpdating}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Evidence Modal */}
        {selectedOpportunity && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '16px',
            }}
            onClick={() => setSelectedOpportunity(null)}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                maxWidth: '680px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
                padding: '28px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ ...getPriorityBadgeStyle(selectedOpportunity.priority), padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                      {selectedOpportunity.priority}
                    </span>
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                      {getCategoryLabel(selectedOpportunity.category)}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#0f172a' }}>
                    {selectedOpportunity.title}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedOpportunity(null)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer', padding: '4px' }}
                >
                  
                </button>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.88rem' }}>
                {selectedOpportunity.targetKeyword && (
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Target Query:</strong> "{selectedOpportunity.targetKeyword}"
                  </div>
                )}
                {selectedOpportunity.targetUrl && (
                  <div style={{ wordBreak: 'break-all' }}>
                    <strong>Target URL:</strong> {selectedOpportunity.targetUrl}
                  </div>
                )}
                <div>
                  <strong>Data Source:</strong> {String(selectedOpportunity.evidenceJson.source || 'DATABASE')}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Raw Stored Evidence Payload
                </div>
                <pre
                  style={{
                    backgroundColor: '#0f172a',
                    color: '#38bdf8',
                    padding: '16px',
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    lineHeight: 1.5,
                  }}
                >
                  {JSON.stringify(selectedOpportunity.evidenceJson, null, 2)}
                </pre>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setSelectedOpportunity(null)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Weekly Executive AI Summary Modal */}
        {showWeeklySummaryModal && weeklySummary && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '16px',
            }}
            onClick={() => setShowWeeklySummaryModal(false)}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                maxWidth: '720px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
                padding: '28px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                    EXECUTIVE SUMMARY
                  </span>
                  <h2 style={{ margin: '8px 0 0', fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                    Weekly Local SEO Performance & Priorities
                  </h2>
                </div>
                <button
                  onClick={() => setShowWeeklySummaryModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer', padding: '4px' }}
                >
                  
                </button>
              </div>

              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '4px' }}>Performance Overview:</strong>
                <p style={{ margin: 0, fontSize: '0.92rem', color: '#334155', lineHeight: 1.5 }}>
                  {weeklySummary.performanceSummary}
                </p>
              </div>

              {weeklySummary.importantChanges.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#0f172a', fontSize: '0.92rem' }}>Key Observations & Changes:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: '20px', fontSize: '0.88rem', color: '#334155' }}>
                    {weeklySummary.importantChanges.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              {weeklySummary.recommendedPriorities.length > 0 && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '14px', borderRadius: '8px', marginBottom: '16px' }}>
                  <strong style={{ color: '#166534', fontSize: '0.92rem' }}>Recommended Priorities for Upcoming Week:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: '20px', fontSize: '0.88rem', color: '#14532d' }}>
                    {weeklySummary.recommendedPriorities.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => handleWeeklySummary(true)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #2563eb', background: '#2563eb', color: '#fff', fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  Regenerate Fresh
                </button>
                <button
                  onClick={() => setShowWeeklySummaryModal(false)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
